import { MouseEvent } from 'react';

import {
  MODULE_STYLES,
  isGoogleDocUrl,
  pathToModuleStyle,
} from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';

export const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

export const isExternalLink = (uri: string): boolean =>
  /^https?:\/\//i.test(uri);

export const isExternalDocument = (uri: string): boolean =>
  uri.replace(/^\/\//, '').startsWith('externalDocuments/');

export const isCollaborativeDocument = (uri: string): boolean =>
  uri.replace(/^\/\//, '').startsWith('collaborativeDocuments/');

export const isCodingSessionUri = (uri: string): boolean =>
  /^(?:\/\/)?codingSessions\/[^/]+\/[^/]+$/.test(uri);

const VAULT_PREFIX = '[Vault] ';

export const isVaultArtifact = (artifact: AttachmentFragment): boolean =>
  artifact.displayName?.startsWith(VAULT_PREFIX) ?? false;

export const getArtifactDisplayName = (
  artifact: AttachmentFragment,
  fallback: string = '',
): string => {
  if (isVaultArtifact(artifact) && artifact.displayName) {
    return artifact.displayName.slice(VAULT_PREFIX.length);
  }
  return artifact.displayName || fallback;
};

export const isRenamableArtifact = (artifact: AttachmentFragment): boolean => {
  const { uri } = artifact;
  if (isGoogleDocUrl(uri)) return false;
  if (isExternalLink(uri)) return true;
  if (isExternalDocument(uri)) return true;
  if (isCollaborativeDocument(uri)) return true;
  return false;
};

export const createModifierClickHandler =
  (href: string | undefined, onSelect: () => void) => (event: MouseEvent) => {
    if ((event.metaKey || event.ctrlKey) && href) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    onSelect();
  };

export type ArtifactCategory = 'artifact' | 'context';

export const getArtifactCategory = (
  artifact: AttachmentFragment,
): ArtifactCategory =>
  isExternalLink(artifact.uri) ||
  isExternalDocument(artifact.uri) ||
  isCodingSessionUri(artifact.uri)
    ? 'context'
    : 'artifact';

export const ARTIFACT_GROUPS = [
  { key: 'abtests', label: 'A/B tests' },
  { key: 'rollouts', label: 'Rollouts' },
  { key: 'analysis', label: 'Analyses' },
  { key: 'flags', label: 'Flags' },
  { key: 'metrics', label: 'Metrics' },
  { key: 'segments', label: 'Segments' },
  { key: 'events', label: 'Events' },
  { key: 'rules', label: 'Rules' },
  { key: 'issues', label: 'Issues' },
  { key: 'pulses', label: 'Pulses' },
  { key: 'vault', label: 'Vault' },
  { key: 'documents', label: 'Documents' },
  { key: 'codingSessions', label: 'Coding sessions' },
  { key: 'links', label: 'Links' },
  { key: 'other', label: 'Other' },
] as const;

export type ArtifactGroupKey = (typeof ARTIFACT_GROUPS)[number]['key'];

export interface ArtifactGroup {
  key: ArtifactGroupKey;
  label: string;
  artifacts: AttachmentFragment[];
}

const MODULE_KEY_TO_GROUP: Partial<Record<string, ArtifactGroupKey>> = {
  abtests: 'abtests',
  rollouts: 'rollouts',
  analysis: 'analysis',
  flags: 'flags',
  metrics: 'metrics',
  segments: 'segments',
  events: 'events',
  issues: 'issues',
  pulses: 'pulses',
};

const MODULE_LABEL_TO_GROUP: Record<string, ArtifactGroupKey> = {
  ...Object.fromEntries(
    Object.entries(MODULE_STYLES)
      .filter(([key]) => key in MODULE_KEY_TO_GROUP)
      .map(([key, style]) => [style.label, MODULE_KEY_TO_GROUP[key]]),
  ),
  Rule: 'rules',
  Document: 'documents',
};

export const getArtifactGroupKey = (
  artifact: AttachmentFragment,
): ArtifactGroupKey => {
  if (isVaultArtifact(artifact)) return 'vault';

  const { uri } = artifact;

  if (isExternalLink(uri)) {
    return isGoogleDocUrl(uri) ? 'documents' : 'links';
  }
  if (isCodingSessionUri(uri)) return 'codingSessions';
  if (isExternalDocument(uri)) return 'documents';
  if (isCollaborativeDocument(uri)) return 'documents';

  const style = pathToModuleStyle(uri);
  if (style) {
    return MODULE_LABEL_TO_GROUP[style.label] ?? 'other';
  }

  return 'other';
};

export const groupArtifactsByType = (
  artifacts: ReadonlyArray<AttachmentFragment>,
): ArtifactGroup[] => {
  const grouped = new Map<ArtifactGroupKey, AttachmentFragment[]>();

  for (const artifact of artifacts) {
    const key = getArtifactGroupKey(artifact);
    const list = grouped.get(key);
    if (list) {
      list.push(artifact);
    } else {
      grouped.set(key, [artifact]);
    }
  }

  return ARTIFACT_GROUPS.flatMap(({ key, label }) => {
    const items = grouped.get(key);
    if (!items) return [];
    return [{ key, label, artifacts: items }];
  });
};
